import { useMutation } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { useAppDispatch } from '@/store/store';
import { clearSession } from '@/lib/auth/clearSession';
import {
  setAccessToken,
  setRefreshToken,
  setUser,
  setError,
  setLoading,
  AuthUser,
} from '@/store/slices/authSlice';
import { appsFlyerService } from '@/lib/appsflyer';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

// Types
export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  access: string;
  refresh: string;
  user: AuthUser;
}

export interface RegisterCredentials {
  firstName: string;
  lastName: string;
  email: string;
  location: string;
  dob: string;
  bio: string;
  password: string;
  confirmPassword: string;
  /** Whether the user consents to displaying their email on their public profile. */
  shareEmailOnProfile?: boolean;
}

export interface RegisterResponse {
  access: string;
  refresh: string;
  user: AuthUser;
}

export interface ForgotPasswordRequest {
  email: string;
  /**
   * Deep-link base URL for the reset email. The backend appends `?code=uid@token`.
   * The API client transforms this to redirect_url before sending.
   */
  redirectUrl?: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

/**
 * Hook for user login
 * On success: stores tokens in Redux and localStorage
 * On error: sets error state in Redux
 */
export function useLogin() {
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) =>
      apiFetch<LoginResponse>('auth/login/', {
        method: 'POST',
        body: credentials,
      }),
    onMutate: () => {
      dispatch(setLoading(true));
      dispatch(setError(null));
    },
    onSuccess: (data) => {
      if (!data.access || !data.refresh) {
        dispatch(setError('Login failed: One or more tokens are missing'));
        return;
      }
      dispatch(setAccessToken(data.access));
      dispatch(setRefreshToken(data.refresh));
      if (data.user) {
        dispatch(setUser(data.user));

        // AppsFlyer: Set customer user ID and log login event
        if (data.user.id != null) {
          appsFlyerService.setCustomerUserId(String(data.user.id));
        }
        appsFlyerService.logLogin();
        // Send AppsFlyer ID to backend for S2S events
        appsFlyerService.sendAppsFlyerIdToBackend();

        // PostHog: Track login completed
        analytics.capture(AnalyticsEvents.LOGIN_COMPLETED, {
          method: 'email',
        });
      }
      dispatch(setLoading(false));
    },
    onError: (error: any) => {
      console.error('An error occurred while logging in:', error);
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          error.message ||
                          'Login failed';
      dispatch(setError(errorMessage));
      dispatch(setLoading(false));
    },
  });
}

/**
 * Hook for user logout
 * Clears all auth state from Redux and localStorage, and clears the query cache
 */
export function useLogout() {
  return useMutation({
    mutationFn: async () => {
      await clearSession();
    },
  });
}

/**
 * Hook for user registration
 * Creates a new user account and auto-logs in by storing JWT tokens
 */
export function useRegister() {
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: (credentials: RegisterCredentials) =>
      apiFetch<RegisterResponse>('auth/register/', {
        method: 'POST',
        body: credentials,
      }),
    onMutate: () => {
      dispatch(setLoading(true));
      dispatch(setError(null));
    },
    onSuccess: (data) => {
      // Validate tokens exist (matching useLogin pattern)
      if (!data.access || !data.refresh) {
        dispatch(setError('Registration failed: One or more tokens are missing'));
        dispatch(setLoading(false));
        return;
      }

      // Store tokens (same as login)
      dispatch(setAccessToken(data.access));
      dispatch(setRefreshToken(data.refresh));
      if (data.user) {
        dispatch(setUser(data.user));

        // AppsFlyer: Set customer user ID and log registration event
        if (data.user.id != null) {
          appsFlyerService.setCustomerUserId(String(data.user.id));
        }
        appsFlyerService.logRegistration('email');
        appsFlyerService.sendAppsFlyerIdToBackend();
      }
      dispatch(setLoading(false));

      // PostHog: Track signup completed
      analytics.capture(AnalyticsEvents.SIGNUP_COMPLETED, {
        method: 'email',
      });
    },
    onError: (error: any) => {
      console.error('An error occurred during registration:', error);
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          error.message ||
                          'Registration failed';
      dispatch(setError(errorMessage));
      dispatch(setLoading(false));
    },
  });
}

/**
 * Hook for forgot password.
 * Sends a password reset email to the member. The payload includes a
 * `redirectUrl` so the backend can embed the correct deep-link into the email
 * (e.g. window.location.origin + '/login').
 * The API client transforms camelCase → snake_case automatically.
 */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (data: ForgotPasswordRequest) =>
      apiFetch<ForgotPasswordResponse>('auth/password/reset/', {
        method: 'POST',
        body: data,
      }),
  });
}

export interface ResetPasswordRequest {
  uid: string;
  token: string;
  /** Transformed to new_password1 by the API client before sending. */
  newPassword1: string;
  /** Transformed to new_password2 by the API client before sending. */
  newPassword2: string;
}

export interface ResetPasswordResponse {
  detail: string;
}

/**
 * Hook for resetting a password using the uid+token from the reset email.
 * Payload fields are camelCase — the API client transforms them to snake_case
 * before sending (newPassword1 → new_password1, newPassword2 → new_password2).
 * The numeric suffix is preserved correctly by the camelToSnake regex.
 *
 * NOTE: Error bodies are camelCase too — the backend's CamelCaseJSONRenderer
 * renders error responses as well (e.g. data?.newPassword1, data?.nonFieldErrors).
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: (data: ResetPasswordRequest) =>
      apiFetch<ResetPasswordResponse>('auth/password/reset/confirm/', {
        method: 'POST',
        body: data,
      }),
  });
}
